/**
 * InviteMemberDialog.tsx — 邀请团队成员弹窗（已接真，ADM 运营账号邀请制）
 *
 * 移植自原型 design/Novel-admin/src/dialogs2.jsx（注册名 invite-member），并按契约接真：
 * 提交走 POST /api/v1/auth/invite（use-auth.inviteMember），契约 InviteRequestSchema={email,nickname,role}。
 * 后端 requireRole('root','admin')，且仅 root 可邀请 root。
 *
 * 与原型的契约偏离（已记 docs/plans/2026-06-12-web-real-api-integration-plan.md §三 settings #1 / §七）：
 * - 原型缺 nickname 输入 → 契约必填，本弹窗补「昵称」行（每个邀请一条 email+nickname）。
 * - 原型「批量邮箱」一次邀多人，但契约昵称随 email 一一对应 → 改为「逐条邀请」（email+nickname+role），
 *   逐条 POST（Promise.allSettled 聚合成功/失败计数），失败不假成功。
 * - 角色为 4 角色闭集（root/admin/operator/viewer）；root 选项仅 root 会话可选（前端门控，后端权威）。
 * - 原型「可访问 App / 附加备注」契约 invite 端点无对应字段 → 移除（不发杜撰字段，checklist #4）。
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { inviteMember } from '~/hooks/use-auth';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UiFormRow } from '~/ui/FormRow';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';

/** 弹窗布局行：共享 ~/ui/FormRow 收口弹窗尺寸，对齐原型 dialogs2 包装 */
const FormRow = (p: FormRowProps) => (
  <UiFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

/** 4 角色闭集中文标签（root/admin/operator/viewer，与 P0-admin-auth-rbac 一致）*/
const ROLE_LABELS: { value: 'root' | 'admin' | 'operator' | 'viewer'; label: string }[] = [
  { value: 'admin', label: 'Admin（全权，不含 root 专属）' },
  { value: 'operator', label: '运营（书籍/章节/推送/反馈/Banner/FAQ）' },
  { value: 'viewer', label: '只读（仅查看，所有写接口拒绝）' },
  { value: 'root', label: 'Root（全权 + 账号管理 / 白名单 / BONUS）' },
];

/** 一条待邀请行（email + nickname；契约要求一一对应）*/
interface InviteRow {
  email: string;
  nickname: string;
}

export function InviteMemberDialog({ onClose }: { onClose: () => void }) {
  // 邀请权限：后端 requireRole('root','admin')；非 root/admin 不该到达此弹窗，但弹窗自身再门控一道
  const currentRole = useAuthStore((s) => s.user?.role);
  const canInvite = currentRole === 'root' || currentRole === 'admin';
  const isRoot = currentRole === 'root';

  const [rows, setRows] = useState<InviteRow[]>([{ email: '', nickname: '' }]);
  const [role, setRole] = useState<'root' | 'admin' | 'operator' | 'viewer'>('operator');
  const queryClient = useQueryClient();

  const inviteMutation = useMutation({
    mutationFn: async (payload: { rows: InviteRow[]; role: typeof role }) => {
      // 逐条真实 POST；allSettled 聚合成功/失败计数（部分失败不吞，checklist #2）
      const results = await Promise.allSettled(
        payload.rows.map((r) =>
          inviteMember({ email: r.email.trim(), nickname: r.nickname.trim(), role: payload.role }),
        ),
      );
      const fulfilled = results.filter((r) => r.status === 'fulfilled').length;
      const rejected = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      return { sent: fulfilled, failed: rejected.length, firstError: rejected[0]?.reason };
    },
    onSuccess: async ({ sent, failed, firstError }) => {
      if (sent > 0) {
        toast(failed > 0 ? `已发送 ${sent} 个邀请，${failed} 个失败` : `已发送 ${sent} 个邀请`, {
          tone: failed > 0 ? 'warn' : 'pos',
          icon: failed > 0 ? 'IconAlert' : 'IconMail',
        });
        // 邀请落 admin_invites（接受后才建 admin_users 行）→ 刷新「待接受邀请」列表
        await queryClient.invalidateQueries({ queryKey: ['pending-invites'] });
        onClose();
      } else {
        // 全部失败：透出首个错误信息（不假成功）
        toast(firstError instanceof Error ? firstError.message : '邀请发送失败', {
          tone: 'neg',
          icon: 'IconAlert',
        });
      }
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : '邀请发送失败', {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  // 提交前预检：每行 email + nickname 非空（服务端 Zod 为权威）
  const send = () => {
    const valid = rows.filter((r) => r.email.trim() && r.nickname.trim());
    if (!valid.length) {
      toast('请填写邮箱与昵称', { tone: 'warn', icon: 'IconAlert' });
      return;
    }
    inviteMutation.mutate({ rows: valid, role });
  };

  const roleOptions = isRoot ? ROLE_LABELS : ROLE_LABELS.filter((r) => r.value !== 'root');

  return (
    <Dialog
      width={520}
      title="邀请成员"
      subtitle="被邀请者通过邮箱链接接受邀请 · 设密码 + 绑定 2FA 后激活"
      icon="IconUsers"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            邀请链接 24 小时后过期
          </div>
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            onClick={send}
            disabled={!canInvite || inviteMutation.isPending}
            title={canInvite ? undefined : '仅 root / admin 可邀请成员（后端 requireRole）'}
          >
            <Icons.IconPaperPlane size={11} />
            发送邀请
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow label="成员" required hint="每行一个 · 邮箱 + 昵称（契约必填）">
          <div className="vstack" style={{ gap: 6 }}>
            {rows.map((r, i) => (
              <div key={i} className="hstack" style={{ gap: 6 }}>
                <input
                  className="input"
                  value={r.email}
                  onChange={(ev) =>
                    setRows(rows.map((x, j) => (j === i ? { ...x, email: ev.target.value } : x)))
                  }
                  placeholder="name@rivmoon.com"
                  disabled={!canInvite}
                />
                <input
                  className="input"
                  value={r.nickname}
                  onChange={(ev) =>
                    setRows(rows.map((x, j) => (j === i ? { ...x, nickname: ev.target.value } : x)))
                  }
                  placeholder="昵称"
                  style={{ maxWidth: 140 }}
                  disabled={!canInvite}
                />
                {rows.length > 1 && (
                  <button
                    className="btn btn-ghost btn-icon"
                    onClick={() => setRows(rows.filter((_, j) => j !== i))}
                  >
                    <Icons.IconX size={12} />
                  </button>
                )}
              </div>
            ))}
            <button
              className="btn btn-sm btn-ghost"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setRows([...rows, { email: '', nickname: '' }])}
              disabled={!canInvite}
            >
              <Icons.IconPlus size={11} />
              新增一行
            </button>
          </div>
        </FormRow>
        <FormRow label="角色" required>
          <Select
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as typeof role)}
            style={{ maxWidth: 360 }}
            disabled={!canInvite}
          >
            {roleOptions.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
        </FormRow>
      </div>
    </Dialog>
  );
}
